import { ComponentProps, ReactNode, useEffect, useState } from 'react';
import {
  Control,
  Controller,
  FieldValues,
  Path,
  SubmitErrorHandler,
  SubmitHandler,
  useForm,
} from 'react-hook-form';

import { yupResolver } from '@hookform/resolvers/yup';
import { colors, semanticColors } from '@revenuemarket/design';
import {
  Checkbox,
  ErrorMessage,
  Input,
  ModalPdf,
  Toast,
  Typography,
  Tooltip,
} from '@revenuemarket/ui';
import { SystemInfo } from '@revenuemarket/ui/Icon';
import { LandingTypography } from '@revenuemarket/ui/Typography';
import { useRouter } from 'next/router';
import { CalculatorFormData, isPayoutEstimationCode } from '~apis/estimate-calculate';
import { MIXPANEL_EVENT } from '~libs/mixpanel/event';
import { mixpanel } from '~pages/_app';

import {
  DecorationImage,
  CheckboxWrapper,
  InputFieldWrapper,
  Label,
  Form,
  Button,
} from './CalculatorForm.style';
import { CALCULATOR_FORM_SCHEME, formatNumber, parseNumber } from './CalculatorForm.utils';
// TODO: Refactor to resolve circular dependency
// oxlint-disable-next-line import/no-cycle
import CalculatorResult from './CalculatorResult';
import useCalculatorForm from './useCalculatorForm';

const getQueryItemAsString = (queryItem: string | string[] | undefined) => {
  if (Array.isArray(queryItem)) {
    return decodeURIComponent(queryItem[0]);
  }

  return decodeURIComponent(queryItem || '');
};

const CalculatorForm = () => {
  const { companyName, userName, userPosition, email } = useRouter().query;
  const {
    handleSubmit,
    control,
    setValue,
    formState: { errors },
  } = useForm<CalculatorFormData>({
    resolver: yupResolver(CALCULATOR_FORM_SCHEME),
    defaultValues: {
      agreeToPersonalInfoCollection: false,
    },
  });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState<CalculatorFormData>();

  const { payoutEstimationMutation } = useCalculatorForm();

  useEffect(() => {
    setValue('companyName', getQueryItemAsString(companyName));
    setValue('userName', getQueryItemAsString(userName));
    setValue('userPosition', getQueryItemAsString(userPosition));
    setValue('email', getQueryItemAsString(email));
  }, [companyName, userName, userPosition, email, setValue]);

  useEffect(() => {
    if (
      payoutEstimationMutation.data?.Data?.data?.id &&
      payoutEstimationMutation.data?.Data?.code
    ) {
      mixpanel.track('View result', {
        result: payoutEstimationMutation.data?.Data?.code,
      });
    }
  }, [payoutEstimationMutation.data?.Data?.data?.id]);

  const onSubmit: SubmitHandler<CalculatorFormData> = (data) => {
    setFormData({ ...formData, ...data });

    payoutEstimationMutation.mutateAsync(data).then(() => {
      mixpanel.track('Submit calculator');
    });
  };

  const handleError: SubmitErrorHandler<CalculatorFormData> = (error) => {
    const hasOnlyPersonalInfoCollectionError =
      Object.keys(error).length === 1 && error.agreeToPersonalInfoCollection;

    if (hasOnlyPersonalInfoCollectionError) {
      Toast.error('개인정보 수집에 동의해주세요.');
    }
  };

  const handleSubmitRecalculate = (
    data: Pick<CalculatorFormData, 'monthlyAverageSpend' | 'cashAtHand' | 'monthlyAverageRevenue'>,
  ) => {
    if (!formData) return;

    mixpanel.track('Click button - retry calculator');

    onSubmit({ ...formData, ...data });
  };
  const data = payoutEstimationMutation.data;

  if (payoutEstimationMutation.isLoading) {
    return null;
  }

  if (data && formData && isPayoutEstimationCode(data.Data?.code)) {
    return (
      <CalculatorResult
        result={data.Data}
        data={formData}
        onRecalculate={handleSubmitRecalculate}
      />
    );
  }

  return (
    <Form onSubmit={handleSubmit(onSubmit, handleError)}>
      <ModalPdf
        url="https://intercom.help/clobe/ko/articles/9705524-%EA%B0%9C%EC%9D%B8%EC%A0%95%EB%B3%B4-%EC%88%98%EC%A7%91-%EB%B0%8F-%EC%9D%B4%EC%9A%A9%EB%8F%99%EC%9D%98%EC%84%9C"
        isOpen={isModalOpen}
        handleClose={() => setIsModalOpen(false)}
      />
      <DecorationImage src="https://static.clobe.ai/bridge_icon_new_2.png" />
      <InputField
        control={control}
        errorMessage={errors?.userName?.message}
        required
        label="이름"
        placeholder="김마켓"
        name="userName"
      />
      <InputField
        control={control}
        errorMessage={errors?.companyName?.message}
        required
        label="회사명"
        placeholder="주식회사 버티카"
        name="companyName"
      />
      <InputField
        control={control}
        errorMessage={errors?.userPosition?.message}
        required
        label="직함"
        placeholder="대표, CFO 등"
        name="userPosition"
      />
      <InputField
        control={control}
        errorMessage={errors?.email?.message}
        required
        label="회사 이메일"
        placeholder="support@clobe.ai"
        name="email"
      />
      <InputField
        control={control}
        errorMessage={errors?.monthlyAverageRevenue?.message}
        name="monthlyAverageRevenue"
        required
        type="number"
        align="right"
        label={
          <div data-tip data-for="monthlyAverageRevenue" style={{ display: 'flex', gap: '2px' }}>
            월평균 매출{' '}
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
              }}
            >
              <SystemInfo width={12} fill={colors.grayscaleWhite} />
            </div>
            <Tooltip effect="solid" id="monthlyAverageRevenue" place="right">
              회사의 최근 12개월 월평균 매출액
            </Tooltip>
          </div>
        }
        placeholder="100,000,000"
      />
      <InputField
        control={control}
        errorMessage={errors?.monthlyAverageSpend?.message}
        name="monthlyAverageSpend"
        required
        type="number"
        align="right"
        label={
          <div data-tip data-for="cash-burn" style={{ display: 'flex', gap: '2px' }}>
            월평균 비용{' '}
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
              }}
            >
              <SystemInfo width={12} fill={colors.grayscaleWhite} />
            </div>
            <Tooltip effect="solid" id="cash-burn" place="right">
              회사의 최근 12개월 월평균 영업 및 영업외비용
            </Tooltip>
          </div>
        }
        placeholder="10,000,000"
      />
      <InputField
        control={control}
        errorMessage={errors?.cashAtHand?.message}
        name="cashAtHand"
        required
        type="number"
        align="right"
        label={
          <div data-tip data-for="cashAtHand" style={{ display: 'flex', gap: '2px' }}>
            현재 보유 현금{' '}
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
              }}
            >
              <SystemInfo width={12} fill={colors.grayscaleWhite} />
            </div>
            <Tooltip effect="solid" id="cashAtHand" place="right">
              회사가 현재 보유한 입출금 및 외화 잔고 총액 또는 3개월 평균 잔고
            </Tooltip>
          </div>
        }
        placeholder="10,000,000"
      />
      <CheckboxWrapper>
        <Controller
          name="agreeToPersonalInfoCollection"
          control={control}
          render={({ field }) => (
            <Checkbox
              {...field}
              value={field.value + ''}
              id="agreeToPersonalInfoCollection"
              label={
                <Typography variant="caption1" color={colors.grayscale400}>
                  (필수) 개인정보 수집·이용 동의{' '}
                  <span
                    style={{ textDecoration: 'underline' }}
                    onClick={() => setIsModalOpen(true)}
                  >
                    개인정보 처리 방침
                  </span>
                </Typography>
              }
            />
          )}
        />
      </CheckboxWrapper>

      <Button
        onClick={() => {
          window.fbq('track', `[Click] ${MIXPANEL_EVENT.ESTIMATE_CALCULATE}`);
          mixpanel.track(MIXPANEL_EVENT.ESTIMATE_CALCULATE);
        }}
      >
        <LandingTypography variant="h6" color={colors.grayscale800}>
          한도 확인하기
        </LandingTypography>
      </Button>
    </Form>
  );
};

export default CalculatorForm;

interface Props<T extends FieldValues>
  extends React.InputHTMLAttributes<HTMLInputElement>, ComponentProps<typeof Input> {
  required?: boolean;
  label: ReactNode;
  align?: 'left' | 'right';
  type?: 'text' | 'number';
  control: Control<T>;
  name: Path<T>;
  errorMessage?: string;
}

export const InputField = <T extends FieldValues>(props: Props<T>) => {
  const {
    required = false,
    align = 'left',
    type = 'text',
    label,
    control,
    errorMessage,
    ...inputProps
  } = props;

  return (
    <Controller<T>
      control={control}
      name={inputProps.name}
      render={({ field }) => {
        return (
          <InputFieldWrapper hasValue={!!field.value} className={align}>
            <Label variant="body1" required={required} color={'black'} weight={400}>
              {label}
            </Label>
            <Input
              {...field}
              {...inputProps}
              value={type === 'number' ? formatNumber(field.value) : field.value}
              onChange={(event) => {
                if (type === 'number') {
                  return field.onChange(parseNumber(event.target.value));
                }

                field.onChange(event);
              }}
              wrapperClassName={'input-wrapper' + ` ${align}` + (errorMessage ? ' error' : '')}
              className={align}
              style={{
                color: 'black',
                borderColor: errorMessage ? semanticColors.danger.medium : colors.grayscale200,
              }}
            />
            {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
          </InputFieldWrapper>
        );
      }}
    />
  );
};
